import * as yup from 'yup';
import { translations } from '@/translations/index';

type TranslationParams = Record<string, string | number>;

const getCurrentLanguage = (): keyof typeof translations => {
  if (typeof window !== 'undefined') {
    const domain = window.location.hostname;
    if (domain === 'app.aidasales.net') {
      return 'en';
    }
    if (domain === 'app.aidasales.ir') {
      return 'fa';
    }
    const storedLanguage = localStorage.getItem('language');
    if (storedLanguage && storedLanguage in translations) {
      return storedLanguage as keyof typeof translations;
    }
  }

  return 'fa';
};

const translate = (key: string, params?: TranslationParams): string => {
  const currentLanguage = getCurrentLanguage();
  const keys = key.split('.');
  let value: any = translations[currentLanguage];

  for (const k of keys) {
    if (value && typeof value === 'object') {
      value = value[k];
    } else {
      return key;
    }
  }

  if (typeof value === 'string' && params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      value = value.replace(new RegExp(`{${paramKey}}`, 'g'), String(paramValue));
    });
  }

  return typeof value === 'string' ? value : key;
};


// Utility function to convert Persian numbers to English numbers
export const convertPersianToEnglishNumbers = (text: string): string => {
  const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  
  let result = text;
  for (let i = 0; i < persianNumbers.length; i++) {
    result = result.replace(new RegExp(persianNumbers[i], 'g'), englishNumbers[i]);
  }
  
  return result;
};

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'] as const;
const ENGLISH_DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

/** Convert Western/Arabic digits in a string to Persian (۰–۹). */
export const toPersianDigits = (text: string | null | undefined): string => {
  if (text == null) {
    return '';
  }
  let result = String(text);
  for (let i = 0; i < ENGLISH_DIGITS.length; i++) {
    result = result.replace(new RegExp(ENGLISH_DIGITS[i], 'g'), PERSIAN_DIGITS[i]);
  }
  const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  for (let i = 0; i < arabicDigits.length; i++) {
    result = result.replace(new RegExp(arabicDigits[i], 'g'), PERSIAN_DIGITS[i]);
  }
  return result;
};

/** Format a number with Persian digits and locale-aware grouping. */
export const formatPersianNumber = (value: number | null | undefined): string =>
  new Intl.NumberFormat('fa-IR').format(value ?? 0);

export const phoneNumberSchema = yup
  .string()
    .matches(/^\+98[0-9]{10}$/, translate('validation.phoneValidationPattern'))
    .required(translate('validation.phoneValidationRequired'));

export const emailSchema = yup
  .string()
  .email(translate('validation.emailValidation'))
  .required(translate('validation.emailValidationRequired'));

export const otpSchema = yup
  .string()
  .matches(/^[0-9]{6}$/, translate('validation.otpValidationPattern'))
  .required(translate('validation.otpValidationRequired'));

export const personalInfoSchema = yup.object().shape({
  fullName: yup
    .string()
    .min(3, translate('validation.fullNameValidationMin'))
    .required(translate('validation.fullNameValidationRequired')),
  phoneNumber: phoneNumberSchema,
  position: yup
    .string()
    .min(2, translate('validation.positionValidationMin'))
    .required(translate('validation.positionValidationRequired')),
});

export const businessInfoSchema = yup.object().shape({
  businessName: yup
    .string()
    .min(3, translate('validation.businessNameValidationMin'))
    .required(translate('validation.businessNameValidationRequired')),
  businessType: yup.string().required(translate('validation.businessTypeValidationRequired')),
  businessSize: yup.string().required(translate('validation.businessSizeValidationRequired')),
  businessDescription: yup
    .string()
    .min(10, translate('validation.businessDescriptionValidationMin'))
    .required(translate('validation.businessDescriptionValidationRequired')),
});

export const serviceInfoSchema = yup.object().shape({
  serviceType: yup.string().required(translate('validation.serviceTypeValidationRequired')),
  serviceDescription: yup
    .string()
    .min(10, translate('validation.serviceDescriptionValidationMin'))
    .required(translate('validation.serviceDescriptionValidationRequired')),
  targetAudience: yup
    .string()
    .min(10, translate('validation.targetAudienceValidationMin'))
    .required(translate('validation.targetAudienceValidationRequired')),
  interactionStyle: yup
    .string()
    .oneOf(['proactive', 'reactive'], translate('validation.interactionStyleValidation'))
    .required(translate('validation.interactionStyleValidationRequired')),
  channels: yup
    .array()
    .of(yup.string())
    .min(1, translate('validation.channelsValidationMin'))
    .required(translate('validation.channelsValidationRequired')),
});

export const instagramInfoSchema = yup.object().shape({
  instagramUsername: yup
    .string()
    .min(3, translate('validation.instagramUsernameValidationMin'))
    .required(translate('validation.instagramUsernameValidationRequired')),
  instagramPassword: yup
    .string()
    .min(6, translate('validation.instagramPasswordValidationMin'))
    .required(translate('validation.instagramPasswordValidationRequired')),
});

export const subscriptionSchema = yup.object().shape({
  plan: yup
    .string()
    .oneOf(['intern', 'expert', 'master'], translate('validation.planValidation'))
    .required(translate('validation.planValidationRequired')),
  duration: yup
    .string()
    .oneOf(['QUARTERLY', 'SEMIANNUAL', 'ANNUAL'], translate('validation.durationValidation'))
    .required(translate('validation.durationValidationRequired')),
}); 

export const phoneNumberValidation = (phoneNumber: string) => {
  // Convert Persian numbers to English first
  const convertedPhone = convertPersianToEnglishNumbers(phoneNumber.trim());
  
  // Case 1: Starting with 09 (should be 11 digits total)
  if (convertedPhone.match(/^09[0-9]{9}$/)) {
    return { isValid: true, normalizedPhone: convertedPhone };
  }
  
  // Case 2: Starting with 989 (should be 12 digits total)
  if (convertedPhone.match(/^989[0-9]{9}$/)) {
    return { isValid: true, normalizedPhone: '+' + convertedPhone };
  }
  
  // Case 3: Starting with +989 (should be 12 digits + the + sign)
  if (convertedPhone.match(/^\+989[0-9]{9}$/)) {
    return { isValid: true, normalizedPhone: convertedPhone };
  }
  
  // If none of the patterns match, return error
  return { 
    // isValid: false, 
    isValid: true, 
    // error: useTranslation().t('validation.phoneValidation'),
  };
};